import React from 'react'
import { Box, Card, CardContent, IconButton, Typography } from '@mui/material'
import { TextFields, Image, PictureAsPdf, Close } from '@mui/icons-material'

let blockTypes = [
  {
    id: 'text' as const,
    name: 'Text',
    description: 'Write & format text',
    icon: TextFields,
    color: 'primary.main',
  },
  {
    id: 'image' as const,
    name: 'Image',
    description: 'Upload an image',
    icon: Image,
    color: 'primary.main',
  },
  {
    id: 'pdf' as const,
    name: 'PDF',
    description: 'Embed a document',
    icon: PictureAsPdf,
    color: 'primary.main',
  },
]

interface BlockCreationPaletteProps {
  onCreateBlock: (type: 'text' | 'image' | 'pdf') => void
  onClose: () => void
  className?: string
  disabled?: boolean
}

export function BlockCreationPalette({
  onCreateBlock,
  onClose,
  className,
  disabled = false,
}: BlockCreationPaletteProps) {
  return (
    <Box
      className={className}
      sx={{
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: 'text.secondary',
          }}
        >
          ADD A BLOCK
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close add block panel"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
        }}
      >
        {blockTypes.map((blockType) => {
          let IconComponent = blockType.icon

          return (
            <Card
              key={blockType.id}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label={`Add ${blockType.name}`}
              aria-disabled={disabled}
              elevation={0}
              sx={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'border-color 0.15s ease',
                '&:hover': disabled ? {} : { borderColor: 'primary.main' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                },
              }}
              onClick={() => {
                if (!disabled) onCreateBlock(blockType.id)
              }}
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  onCreateBlock(blockType.id)
                }
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <IconComponent sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {blockType.name}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {blockType.description}
                </Typography>
              </CardContent>
            </Card>
          )
        })}
      </Box>
    </Box>
  )
}

export default BlockCreationPalette
